import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import "./book-upload-modal.css";
import "./photo-import-modal.css";

interface ExtractedRow {
  title: string;
  author: string | null;
  isbn: string | null;
}
interface EditableRow extends ExtractedRow {
  uid: string;
}

interface ExtractResponse {
  rows: ExtractedRow[];
  usage: { inputTokens: number; outputTokens: number };
}

interface CheckInResult {
  inserted: number;
  failed: Array<{ row: number; errors: string[] }>;
}
interface CheckOutResult {
  collected: number;
  failed: Array<{ row: number; errors: string[] }>;
}

export type PhotoMode = "checkin" | "checkout";

const MODE_CONFIG: Record<PhotoMode, {
  endpoint: string;
  emailColumnHeader: string;
}> = {
  checkin: { endpoint: "/api/admin/books/upload", emailColumnHeader: "donorEmail" },
  checkout: { endpoint: "/api/admin/checkout/upload", emailColumnHeader: "userEmail" },
};

function escapeCsv(v: string): string {
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function rowsToCsv(rows: EditableRow[], emailHeader: string, sharedEmail: string, noCredit: boolean): string {
  const headers =
    emailHeader === "donorEmail"
      ? ["Title", "Author", "ISBN", "donorEmail", "noCredit"]
      : ["Title", "ISBN", "userEmail", "noCredit"];
  const lines = [headers.join(",")];
  for (const r of rows) {
    if (!r.title.trim()) continue; // skip empty
    const email = noCredit ? "" : sharedEmail.trim();
    if (emailHeader === "donorEmail") {
      lines.push(
        [
          escapeCsv(r.title),
          escapeCsv(r.author ?? ""),
          escapeCsv(r.isbn ?? ""),
          escapeCsv(email),
          noCredit ? "true" : "",
        ].join(","),
      );
    } else {
      lines.push(
        [
          escapeCsv(r.title),
          escapeCsv(r.isbn ?? ""),
          escapeCsv(email),
          noCredit ? "true" : "",
        ].join(","),
      );
    }
  }
  return lines.join("\n");
}

let uidCounter = 0;
const newUid = () => `r${Date.now()}-${++uidCounter}`;

export function PhotoImportModal({
  onClose,
  mode = "checkin",
}: {
  onClose: () => void;
  mode?: PhotoMode;
}) {
  const { t } = useTranslation();
  const cfg = MODE_CONFIG[mode];
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [rows, setRows] = useState<EditableRow[] | null>(null);
  const [email, setEmail] = useState("");
  const [noCredit, setNoCredit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CheckInResult | CheckOutResult | null>(null);
  // Live camera state — distinct from file-selected state so we can render
  // a video preview + capture button instead of a static image.
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const title = mode === "checkin" ? t("admin.photo.titleCheckin") : t("admin.photo.titleCheckout");
  const emailLabel = mode === "checkin" ? t("admin.photo.emailLabelCheckin") : t("admin.photo.emailLabelCheckout");
  const noCreditLabel = mode === "checkin" ? t("admin.photo.noCreditCheckin") : t("admin.photo.noCreditCheckout");

  // Stop the camera when the user moves on (file picked, rows received, modal
  // closed). Tracks must be explicitly stopped or the indicator/light stays on.
  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }
  useEffect(() => () => stopCamera(), []);

  async function startCamera() {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(t("admin.photo.cameraNoSupport"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraActive(true);
      // Wait for the next render so videoRef is bound.
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {
            /* autoplay restrictions — playsinline should cover it */
          });
        }
      }, 0);
    } catch (err) {
      const msg = (err as Error).message ?? "";
      if (/denied|permission|notallowed/i.test(msg)) {
        setCameraError(t("admin.photo.cameraDenied"));
      } else if (/notfound|not found|devicenotfound/i.test(msg)) {
        setCameraError(t("admin.photo.cameraNotFound"));
      } else if (/secure|https/i.test(msg)) {
        setCameraError(t("admin.photo.cameraHttps"));
      } else {
        setCameraError(msg || t("admin.photo.cameraStartError"));
      }
    }
  }

  async function snapPhoto() {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const snapped = new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
        stopCamera();
        pickFile(snapped);
      },
      "image/jpeg",
      0.85,
    );
  }

  function pickFile(f: File | null) {
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(f ? URL.createObjectURL(f) : null);
    setRows(null);
    setError(null);
    setResult(null);
  }

  async function onExtract(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setExtracting(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch("/api/admin/photo-extract", {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? `HTTP ${res.status}`);
        return;
      }
      const extracted = (data as ExtractResponse).rows;
      setRows(extracted.map((r) => ({ ...r, uid: newUid() })));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setExtracting(false);
    }
  }

  function updateRow(uid: string, patch: Partial<ExtractedRow>) {
    setRows((cur) => cur?.map((r) => (r.uid === uid ? { ...r, ...patch } : r)) ?? null);
  }
  function removeRow(uid: string) {
    setRows((cur) => cur?.filter((r) => r.uid !== uid) ?? null);
  }
  function addRow() {
    setRows((cur) => [...(cur ?? []), { uid: newUid(), title: "", author: null, isbn: null }]);
  }

  async function onImport() {
    if (!rows || rows.length === 0) return;
    // Both check-in (donor) and check-out (collector) accept an email OR a
    // name — resolved server-side via resolveOrCreateUser. Just require
    // something unless the anonymous toggle is set.
    if (!noCredit && email.trim().length < 2) {
      setError(mode === "checkin" ? t("admin.photo.emailRequiredCheckin") : t("admin.photo.emailRequiredCheckout"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const csv = rowsToCsv(rows, cfg.emailColumnHeader, email, noCredit);
      const blob = new Blob([csv], { type: "text/csv" });
      const csvFile = new File([blob], "photo-import.csv", { type: "text/csv" });
      const form = new FormData();
      form.append("file", csvFile);
      const res = await fetch(cfg.endpoint, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? `HTTP ${res.status}`);
        return;
      }
      setResult(data);
      qc.invalidateQueries({ queryKey: ["admin", "books-list"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
      qc.invalidateQueries({ queryKey: ["books"] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="photo-title">
      <div className="modal modal--wide">
        <h3 id="photo-title">{title}</h3>

        {/* Step 1 — pick image (camera live OR file) */}
        {!rows && !result && (
          <form onSubmit={onExtract} className="modal__form">
            <p className="field__help">{t("admin.photo.intro")}</p>

            {cameraActive ? (
              <div className="photo-import__camera">
                <video
                  ref={videoRef}
                  className="photo-import__video"
                  muted
                  playsInline
                  autoPlay
                  aria-label={t("admin.photo.cameraLiveAria")}
                />
                <div className="photo-import__camera-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={stopCamera}
                  >
                    {t("admin.photo.cancelCamera")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={snapPhoto}
                  >
                    {t("admin.photo.capture")}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="photo-import__source-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={startCamera}
                    disabled={extracting}
                  >
                    {t("admin.photo.takePhoto")}
                  </button>
                  <span className="photo-import__or">{t("admin.photo.or")}</span>
                  <label className="btn btn-secondary photo-import__file-label">
                    {t("admin.photo.chooseFile")}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
                      disabled={extracting}
                      hidden
                    />
                  </label>
                </div>
                {cameraError && (
                  <p className="field__help" role="status">{cameraError}</p>
                )}
                {previewUrl && (
                  <>
                    <img
                      src={previewUrl}
                      alt={t("admin.photo.previewAlt")}
                      className="photo-import__preview"
                    />
                    <p className="field__help">{t("admin.photo.imageReady")}</p>
                  </>
                )}
              </>
            )}

            {error && <p className="field__error" role="alert">{error}</p>}
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  stopCamera();
                  onClose();
                }}
                disabled={extracting}
              >
                {t("common.cancel")}
              </button>
              <button type="submit" className="btn btn-primary" disabled={!file || extracting}>
                {extracting ? t("admin.photo.analyzing") : t("admin.photo.extract")}
              </button>
            </div>
          </form>
        )}

        {/* Step 2 — edit rows */}
        {rows && !result && (
          <div className="photo-import__edit">
            <p>
              <strong>{rows.length}</strong>{" "}
              {t("admin.photo.detected", { count: rows.length })}. {t("admin.photo.verifyHint")}
            </p>

            <div className="field field--check">
              <label>
                <input
                  type="checkbox"
                  checked={noCredit}
                  onChange={(e) => setNoCredit(e.target.checked)}
                />
                <span>{noCreditLabel}</span>
              </label>
            </div>

            {!noCredit && (
              <div className="field">
                <label htmlFor="photo-email">{emailLabel}</label>
                <input
                  id="photo-email"
                  type="text"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("admin.photo.donorPlaceholder")}
                />
                <p className="field__help">
                  {mode === "checkin" ? t("admin.photo.emailHelpCheckin") : t("admin.photo.emailHelpCheckout")}
                </p>
              </div>
            )}

            <div className="photo-import__table-wrap">
              <table className="photo-import__table">
                <thead>
                  <tr>
                    <th>{t("admin.photo.colTitle")}</th>
                    {mode === "checkin" && <th>{t("admin.photo.colAuthor")}</th>}
                    <th>ISBN</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.uid}>
                      <td data-label={t("admin.photo.colTitle")}>
                        <input
                          type="text"
                          value={r.title}
                          onChange={(e) => updateRow(r.uid, { title: e.target.value })}
                          aria-label={t("admin.photo.colTitle")}
                        />
                      </td>
                      {mode === "checkin" && (
                        <td data-label={t("admin.photo.colAuthor")}>
                          <input
                            type="text"
                            value={r.author ?? ""}
                            onChange={(e) => updateRow(r.uid, { author: e.target.value || null })}
                            aria-label={t("admin.photo.colAuthor")}
                          />
                        </td>
                      )}
                      <td data-label="ISBN">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={r.isbn ?? ""}
                          onChange={(e) => updateRow(r.uid, { isbn: e.target.value || null })}
                          aria-label="ISBN"
                          placeholder={t("admin.photo.optional")}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="photo-import__remove"
                          onClick={() => removeRow(r.uid)}
                          aria-label={t("admin.photo.removeRow")}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addRow}>
              {t("admin.photo.addRow")}
            </button>

            {error && <p className="field__error" role="alert">{error}</p>}

            <div className="modal__actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setRows(null);
                  setFile(null);
                  if (previewUrl) URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                }}
                disabled={submitting}
              >
                {t("admin.photo.back")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={onImport}
                disabled={submitting || rows.length === 0}
              >
                {submitting ? t("admin.photo.importing") : t("admin.photo.confirmImport", { count: rows.length })}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — result */}
        {result && (
          <div className="book-upload__result">
            <p>
              <strong>
                {"inserted" in result ? result.inserted : result.collected}
              </strong>{" "}
              {mode === "checkin" ? t("admin.photo.resultImported") : t("admin.photo.resultRecorded")}.
              {result.failed.length > 0 && (
                <>
                  {" "}
                  <strong className="book-upload__fail">{result.failed.length}</strong>{" "}
                  {t("admin.upload.rowsFailed", { count: result.failed.length })}.
                </>
              )}
            </p>
            {result.failed.length > 0 && (
              <ul className="book-upload__errors">
                {result.failed.slice(0, 25).map((f) => (
                  <li key={f.row}>
                    <strong>{t("admin.upload.rowLabel", { row: f.row })}</strong> {f.errors.join("; ")}
                  </li>
                ))}
              </ul>
            )}
            <div className="modal__actions">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                {t("admin.upload.close")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
